import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { Button, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { ModalProps } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getCheckedValuesInObject } from 'utils';
import useRfiGrantAccess from 'hooks/useRfiGrantAccess';

import './GrantAccessModal.styles.scss';

type GrantAccessModalProps = {
  className?: string;
  dataRoomRfiId: string;
  grantAccessDataRoomMemberIds: string[];
  onSuccessfullyGranted: (grantAccessDataRoomMemberIds: string[]) => void;
} & ModalProps;

const GrantAccessModal: ForwardRefRenderFunction<
  OverlayRef,
  GrantAccessModalProps
> = (props, ref) => {
  const {
    className,
    dataRoomRfiId,
    grantAccessDataRoomMemberIds,
    onSuccessfullyGranted,
  } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('grant-access-modal', className);

  const { t } = useTranslation();

  const { membersOptions, membersInitialValue } = useRfiGrantAccess(
    grantAccessDataRoomMemberIds,
  );

  const initialValues = useMemo(
    () => ({
      members: membersInitialValue,
    }),
    [membersInitialValue],
  );

  const handleSubmit = useCallback(
    async ({ members }) => {
      const grantAccessDataRoomMemberIds = getCheckedValuesInObject(members);

      try {
        await dataRoomApi.rfi.patchRfi(dataRoomRfiId, {
          grantAccessDataRoomMemberIds,
        });
        popSuccess(t('succesfullyUpdatedRFIAccess'));
        onSuccessfullyGranted(grantAccessDataRoomMemberIds);
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoomRfiId,
      modal,
      popServerError,
      popSuccess,
      onSuccessfullyGranted,
      t,
    ],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('grantFileAccess')}
      lockScroll={false}
    >
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        render={({ invalid, pristine, submitting, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>
              {t(
                membersOptions.length
                  ? 'chooseDataRoomMembersGrantAccess'
                  : 'noAvailableMembersToGrantAccess',
              )}
            </p>

            <CheckboxFieldGroup
              options={membersOptions}
              name="members"
              direction="column"
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="outline"
                disabled={invalid || pristine || submitting}
              >
                {t('grantAccess')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(GrantAccessModal);
